import {
    BankAccountHoldState,
    BankAccountHoldsModel,
    HoldTypeModel,
    BankAccountHoldFormModel,
    BankAccountHoldUpdateRequest,
    BankAccountHoldModel
} from "@/employer/types";
import { FetchState } from "@/shared/types";
import * as bankservice from "@/shared/api/bankService";
import * as storeHelper from "@/shared/storeHelper";
import { downloadCsvFile } from "@/shared/fileDownload";
import { defineStore } from "pinia";
import _ from "lodash";
import { useNotifierStore } from "@/shared/store/notifier";

const initialState: BankAccountHoldState = {
    username: null,
    holdsFetchState: FetchState.Uninitialized,
    holdTypesFetchState: FetchState.Uninitialized,
    defaultAccountId: null,
    accountHolds: [],
    holdTypeModels: [],
    isVersionConflict: false,
    holdHistoryExportFetchState: FetchState.Uninitialized,
};

export const useBankAccountHoldStore = defineStore("bankAccountHold", {
    state: () => (_.cloneDeep(initialState)),
    getters: {
        holdsLoaded: state => state.holdsFetchState === FetchState.Loaded,
        holdTypesLoaded: state => state.holdTypesFetchState === FetchState.Loaded,
        holdTypes: state => state.holdTypeModels,
        hasAccounts: state => state.defaultAccountId !== null,
        defaultAccountId: state =>
            state.accountHolds.find(a => a.account.accountId === state.defaultAccountId).account.accountId,
        allAccounts: state => state.accountHolds.map(a => a.account),
        holdVersion: state => (accountId: number) =>
            state.accountHolds.find(a => a.account.accountId === accountId).account.accountHoldVersion,
        hasActiveHolds: state => state.accountHolds.filter(a => a.activeHolds.length > 0).length > 0,
        allHolds: state => (accountId: number): BankAccountHoldFormModel[] => {
            const holds = [];
            const holdsForAccount = state.accountHolds.find(a => a.account.accountId === accountId).activeHolds;

            state.holdTypeModels.forEach(holdTypeModel => {
                const holdModel = holdsForAccount.find(h => h.holdType === holdTypeModel.holdTypeId);
                holds.push(
                    {
                        ...holdTypeModel,
                        ...holdModel,
                        scheduledReleaseDate: holdModel && holdModel.scheduledReleaseDate
                            ? holdModel.scheduledReleaseDate
                            : null,
                        isActive: Boolean(holdModel)
                    } as BankAccountHoldFormModel);
            });
            return holds;
        },
        activeHolds () {
            return (accountId: number): BankAccountHoldFormModel[] => {
                return this.allHolds(accountId).filter(h => h.isActive);
            };
        },
        isHoldHistoryExportLoading: state => state.holdHistoryExportFetchState === FetchState.Loading,
    },
    actions: {
        setHoldsFetchState (fetchState: FetchState) {
            this.holdsFetchState = fetchState;
        },
        setHoldTypesFetchState (fetchState: FetchState) {
            this.holdTypesFetchState = fetchState;
        },
        setUsername (username: string) {
            this.username = username;
        },
        setAccountHolds (holdsByAccount: BankAccountHoldsModel[]) {
            this.accountHolds = holdsByAccount;
        },
        setDefaultAccountNumberId (accountId: number) {
            this.defaultAccountId = accountId;
        },
        clearHolds () {
            this.accountHolds = [];
            this.defaultAccountId = null;
        },
        setHoldTypes (holdTypes: HoldTypeModel[]) {
            this.holdTypeModels = holdTypes;
        },
        setIsVersionConflict (isConflict: boolean) {
            this.isVersionConflict = isConflict;
        },
        updateAccountVersion (params: { accountId: number, version: number }) {
            const account = this.accountHolds.find(a => a.account.accountId === params.accountId).account;
            account.accountHoldVersion = params.version;
        },
        updateAccountHolds (params: { accountId: number, holds: BankAccountHoldModel[] }) {
            this.accountHolds.find(a => a.account.accountId === params.accountId).activeHolds = params.holds;
        },
        setHoldHistoryExportFetchState (fetchState: FetchState) {
            this.holdHistoryExportFetchState = fetchState;
        },
        loadHolds (args: { username: string, partnerId: number }) {
            this.clearHolds();
            this.setUsername(args.username);
            this.setHoldsFetchState(FetchState.Loading);

            return bankservice.getAccountHolds(args.username, args.partnerId)
                .then(response => {
                    this.setAccountHolds(response.data.holdsByAccount);
                    this.setDefaultAccountNumberId(response.data.defaultAccountId);
                    this.setIsVersionConflict(false);
                    this.setHoldsFetchState(FetchState.Loaded);
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: false });
                    this.setHoldsFetchState(FetchState.Uninitialized);
                });
        },
        loadHoldTypes () {
            this.setHoldTypesFetchState(FetchState.Loading);
            return bankservice.getHoldTypes()
                .then(response => {
                    this.setHoldTypes(response.data);
                    this.setHoldTypesFetchState(FetchState.Loaded);
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: false });
                    this.setHoldTypesFetchState(FetchState.Uninitialized);
                });
        },
        saveHolds (params: {
            accountId: number,
            request: BankAccountHoldUpdateRequest,
            username: string,
            partnerId: number
        }): Promise<any> {

            return bankservice.putHolds(params.partnerId, params.accountId, params.request)
                .then(response => {
                    this.updateAccountVersion({
                        accountId: response.data.account.accountId,
                        version: response.data.account.accountHoldVersion
                    });
                    this.updateAccountHolds({
                        accountId: response.data.account.accountId,
                        holds: response.data.activeHolds
                    });
                })
                .catch(err => {
                    if (err.response.status === 409) {
                        this.setIsVersionConflict(true);
                        const notifierStore = useNotifierStore();
                        notifierStore.error({ error: "Unable to save holds", rethrow: false });
                        return Promise.reject();
                    }
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        exportHoldHistory (args: { accountId: number, username: string, partnerId: number }) {
            this.setHoldHistoryExportFetchState(FetchState.Loading);
            return bankservice.exportHoldHistory(args.accountId, args.username, args.partnerId).then(response => {
                downloadCsvFile(response.data, "history.csv");
                this.setHoldHistoryExportFetchState(FetchState.Loaded);
            }).catch(err => {
                this.setHoldHistoryExportFetchState(FetchState.Uninitialized);
                return storeHelper.handleError({ error: err, rethrow: true });
            });
        }
    },
});
